import React, { useState, useEffect } from 'react';
import { onAuthStateChanged } from "firebase/auth";
import { collection, addDoc, query, where, getDocs, doc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from '../firebaseConfig/firebase';
import { Container, Form as BootstrapForm, Button, Spinner, Alert } from 'react-bootstrap';
import { Formik, Field, Form as FormikForm, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import Login from './Login';
import Header from './Header';

const opcionesPrioridad = ["GUION", "ARTE", "DIRECCIÓN DE FICCIÓN", "DIRECCIÓN DE NO FICCIÓN", "FOTOGRAFIA", "MONTAJE", "PRODUCCIÓN", "SONIDO"];

const FormInfo = () => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(null);
    const [isFormActive, setIsFormActive] = useState(true);
    const [isUpdateMode, setIsUpdateMode] = useState(false);
    const [submittedData, setSubmittedData] = useState(null); // Nuevo: Datos ya enviados
    const [loadingData, setLoadingData] = useState(true);
    const [alertConfig, setAlertConfig] = useState({ show: false, message: '', variant: 'success' });

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            setIsAuthenticated(!!currentUser);
            setUser(currentUser);

            if (currentUser) {
                try {
                    // 1. Consultar estado del formulario (activo/update)
                    const docControlRef = doc(db, "tribu-form-control", "control");
                    const docControlSnap = await getDoc(docControlRef);
                    if (docControlSnap.exists()) {
                        setIsFormActive(docControlSnap.data().activo_info);
                        setIsUpdateMode(docControlSnap.data().updateForm);
                    }

                    // 2. Consultar si el usuario ya tiene un registro
                    await fetchSubmittedData(currentUser.email);

                } catch (error) {
                    console.error("Error inicializando datos:", error);
                } finally {
                    setLoadingData(false);
                }
            } else {
                setLoadingData(false);
            }
        });

        return () => unsubscribe();
    }, []);

    // Función para recuperar datos (la usaremos al cargar y después de actualizar)
    const fetchSubmittedData = async (email) => {
        const q = query(collection(db, "tribu-form"), where("EMAIL_ALUMNO", "==", email));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
            setSubmittedData(querySnapshot.docs[0].data());
        }
    };

    const handleSubmit = async (values, { resetForm, setSubmitting }) => {
        setAlertConfig({ show: false });
        try {
            // ... (lógica de guardado igual a la tuya)
            await addDoc(collection(db, "tribu-form"), {
                ALUMNO: user?.displayName || "Usuario sin nombre",
                EMAIL_ALUMNO: user?.email,
                PRIORIDAD_1: values.prioridad_1,
                PRIORIDAD_2: values.prioridad_2,
                PRIORIDAD_3: values.prioridad_3,
                fechaCreacion: new Date()
            });

            setAlertConfig({ show: true, message: "¡Enviado con éxito!", variant: 'success' });
            await fetchSubmittedData(user.email); // Refrescar vista
            resetForm();
        } catch (error) {
            setAlertConfig({ show: true, message: "Error al guardar.", variant: 'danger' });
        } finally {
            setSubmitting(false);
        }
    };

    if (isAuthenticated === null || loadingData) return <div className="vh-100 d-flex justify-content-center align-items-center"><Spinner animation="border" /></div>;
    if (!isAuthenticated) return <Login />;

    return (
        <>
            <Container className="mt-5 mb-5">
                <div className="p-4 shadow rounded bg-white">
                    <h2>Preinscripción Especialidad</h2>
                    <p className="text-muted">Usuario: <strong>{user?.email}</strong></p>
                    <hr />

                    {alertConfig.show && (
                        <Alert variant={alertConfig.variant} onClose={() => setAlertConfig({ ...alertConfig, show: false })} dismissible>
                            {alertConfig.message}
                        </Alert>
                    )}

                    {/* --- SECCIÓN DE DATOS ACTUALES (SI EXISTEN) --- */}
                    {submittedData && (
                        <div className="mb-5 p-3 border rounded bg-light">
                            <h5 className="text-primary mb-3">Tus datos registrados:</h5>
                            <div className="row">
                                <div className="col-md-4"><strong>Prioridad 1:</strong> {submittedData.PRIORIDAD_1}</div>
                                <div className="col-md-4"><strong>Prioridad 2:</strong> {submittedData.PRIORIDAD_2}</div>
                                <div className="col-md-4"><strong>Prioridad 3:</strong> {submittedData.PRIORIDAD_3}</div>
                            </div>
                        </div>
                    )}

                    {!isFormActive ? (
                        <Alert variant="info" className="text-center py-4">
                            <h4>Formulario Cerrado</h4>
                            <p>No se aceptan más respuestas en este momento.</p>
                        </Alert>
                    ) : (
                        /* --- FORMULARIO ORIGINAL O BLOQUEO POR YA EXISTIR --- */
                        submittedData ? (
                            <Alert variant="warning" className="text-center">
                                Ya has enviado tu selección de prioridades. Si necesitas cambiar algo, espera al periodo de actualización.
                            </Alert>
                        ) : (
                            /* --- FORMULARIO ORIGINAL DE INSCRIPCIÓN --- */
                            <Formik
                                initialValues={{ prioridad_1: '', prioridad_2: '', prioridad_3: '' }}
                                // Corregido: Validación para evitar que el usuario seleccione la misma prioridad en diferentes campos
                                validationSchema={Yup.object({
                                    prioridad_1: Yup.string().required('Requerido'),
                                    prioridad_2: Yup.string()
                                        .required('Requerido'),
                                    prioridad_3: Yup.string()
                                        .required('Requerido'),
                                })}
                                onSubmit={handleSubmit}
                            >
                                {({ errors, touched, isSubmitting }) => (
                                    <FormikForm>
                                        {[1, 2, 3].map((num) => (
                                            <BootstrapForm.Group className="mb-3" key={num}>
                                                <BootstrapForm.Label className="fw-bold">Prioridad {num}</BootstrapForm.Label>
                                                <Field name={`prioridad_${num}`} as="select" className={`form-select ${touched[`prioridad_${num}`] && errors[`prioridad_${num}`] ? 'is-invalid' : ''}`}>
                                                    <option value="">Selecciona una opción...</option>
                                                    {opcionesPrioridad.map(opcion => <option key={opcion} value={opcion}>{opcion}</option>)}
                                                </Field>
                                                <ErrorMessage name={`prioridad_${num}`} component="div" className="invalid-feedback" />
                                            </BootstrapForm.Group>
                                        ))}
                                        <Button variant="dark" type="submit" className="w-100" disabled={isSubmitting}>
                                            {isSubmitting ? <Spinner size="sm" animation="border" /> : 'Enviar Registro'}
                                        </Button>
                                    </FormikForm>
                                )}
                            </Formik>
                        )
                    )}
                </div>
            </Container>
        </>
    );
};

export default FormInfo;